import React from 'react';
import { AuthProvider, useAuth } from './contexts/global/AuthContext';
import { ThemeProvider } from './contexts/global/ThemeContext';
import { createTodoListContext } from './contexts/local/TodoListFactory';
import { Header } from './components/Header';
import { Login } from './components/Login';
import { TodoList } from './components/TodoList';
import { TodoCategory } from './types';

// Define todo categories - each will get its own LOCAL context
const TODO_CATEGORIES: TodoCategory[] = [
  { id: 'work', name: 'Work', color: '#3b82f6', description: 'Professional tasks and projects' },
  { id: 'personal', name: 'Personal', color: '#10b981', description: 'Personal goals and activities' },
  { id: 'shopping', name: 'Shopping', color: '#f59e0b', description: 'Items to buy and errands' },
  { id: 'health', name: 'Health', color: '#ef4444', description: 'Health and fitness goals' },
];

// Create LOCAL contexts for each category using the factory
const todoContexts = TODO_CATEGORIES.map(category => ({
  category,
  context: createTodoListContext(category)
}));

// Main app content component (needs to be inside AuthProvider to use useAuth)
function AppContent() {
  const { user, isLoading } = useAuth(); // GLOBAL context usage

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <h1>📋 Todo Context Demo</h1>
          <p>Loading application...</p>
          <div className="spinner"></div>
        </div>
        <style>{`
          .loading-screen {
            height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .loading-content {
            text-align: center;
          }
          .loading-content h1 {
            font-size: 2rem;
            margin-bottom: 1rem;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 4px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 2rem auto;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="app">
      <Header />

      <main className="main-content">
        <div className="intro-section">
          <h2>Welcome to the Todo Context Demo! 👋</h2>
          <p>
            This application demonstrates the difference between <strong>Global</strong> and <strong>Local</strong> contexts in React.
          </p>
          
          <div className="context-comparison">
            <div className="context-type global">
              <h3>🌐 Global Contexts</h3>
              <ul>
                <li><strong>Authentication:</strong> User login state affects entire app</li>
                <li><strong>Theme:</strong> Dark/light mode styling for all components</li>
                <li><strong>Wraps:</strong> The entire application at root level</li>
                <li><strong>Shared by:</strong> All components throughout the app</li>
              </ul>
            </div>
            
            <div className="context-type local">
              <h3>📍 Local Contexts</h3>
              <ul>
                <li><strong>Todo Lists:</strong> Each category has isolated state</li>
                <li><strong>Independence:</strong> Actions in one don't affect others</li>
                <li><strong>Wraps:</strong> Only specific component trees</li>
                <li><strong>Created by:</strong> Factory pattern for reusability</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Each todo category gets its own LOCAL context provider */}
        <div className="todo-categories">
          {todoContexts.map(({ category, context }) => (
            <div key={category.id}>
              {/* LOCAL context provider wraps only this specific todo list */}
              <context.Provider>
                <TodoList useTodoList={context.useContext} />
              </context.Provider>
            </div>
          ))}
        </div>

        <div className="demonstration-section">
          <h3>🎯 Try This Demonstration:</h3>
          <ol>
            <li><strong>Theme Toggle:</strong> Click the theme button in header - affects ALL components (Global)</li>
            <li><strong>Add Todos:</strong> Add todos to different categories - they stay isolated (Local)</li>
            <li><strong>Filter:</strong> Change filters in one category - others are unaffected (Local)</li>
            <li><strong>Complete Tasks:</strong> Mark tasks complete - stats update only for that category (Local)</li>
            <li><strong>Logout:</strong> User state affects entire app navigation (Global)</li>
          </ol>
        </div>
      </main>

      <style>{`
        .app {
          min-height: 100vh;
          background: var(--bg-color);
          color: var(--text-color);
        }

        .main-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .intro-section {
          margin-bottom: 3rem;
          text-align: center;
        }

        .intro-section h2 {
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        .intro-section p {
          font-size: 1.125rem;
          color: #6b7280;
          margin-bottom: 2rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
        }

        .context-comparison {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          max-width: 800px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .context-comparison {
            grid-template-columns: 1fr;
          }
        }

        .context-type {
          padding: 1.5rem;
          border-radius: 0.75rem;
          text-align: left;
        }

        .context-type.global {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
        }

        .context-type.local {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
        }

        .context-type h3 {
          margin: 0 0 1rem 0;
          font-size: 1.25rem;
        }

        .context-type ul {
          margin: 0;
          padding-left: 1.5rem;
        }

        .context-type li {
          margin: 0.5rem 0;
          line-height: 1.4;
        }

        .todo-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
          margin-bottom: 3rem;
        }

        @media (max-width: 768px) {
          .todo-categories {
            grid-template-columns: 1fr;
          }
        }

        .demonstration-section {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 0.75rem;
          margin: 3rem 0;
        }

        .demonstration-section h3 {
          margin: 0 0 1rem 0;
          color: #1f2937;
        }

        .demonstration-section ol {
          margin: 0;
          padding-left: 1.5rem;
          color: #374151;
        }

        .demonstration-section li {
          margin: 0.75rem 0;
          line-height: 1.5;
        }

        .demonstration-section strong {
          color: #1f2937;
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    // GLOBAL providers wrap the entire application
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
